/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPost, fetchDelete, fetchPut } from 'FetchAPIs';

const baseURL = '/api/hierarchies/pin';

const messages = defineMessages({
  errorLoadingDashboardPins: 'Error loading dashboard pins. {message}',
  errorLoadingDashboardPinCategories: 'Error loading dashboard pin categories. {message}',
  errorAddingDashboardPin: 'Error adding dashboard pin. {message}',
  errorRemovingDashboardPin: 'Error removing dashboard pin. {message}',
  errorUpdatingDashboardPin: 'Error updating dashboard pin. {message}',
});

export function getDashboardPins(hierarchyPath = '/workspaces') {
  return fetchGet(`${baseURL}${hierarchyPath}/dashboards/pins`).catch(error => {
    throw new I18NError(messages.errorLoadingDashboardPins, {
      message: formatErrorResponse(error),
    });
  });
}

export function getDashboardPinCategories() {
  return fetchGet(`${baseURL}/workspaces/dashboards/pins/categories`).catch(error => {
    throw new I18NError(messages.errorLoadingDashboardPinCategories, {
      message: formatErrorResponse(error),
    });
  });
}

export function addDashboardPin(
  hierarchyPath,
  name,
  dashboardMetaId,
  description,
  category,
  summary,
  favorite = false,
  homePage = false,
  filterJson = null,
  thumbnail = null,
  deviceId,
  userEmailId
) {
  const url = `${baseURL}${hierarchyPath}/dashboards/pins`;

  const pinConfig = {
    description,
    category,
    thumbnail,
    filterJson,
    name,
    dashboardMetaId,
    summary,
    favorite,
    homePage,
    deviceId,
  };
  return fetchPost(url, pinConfig, { userEmailId }).catch(error => {
    throw new I18NError(messages.errorAddingDashboardPin, {
      message: formatErrorResponse(error),
    });
  });
}

export function updateDashboardPin(
  hierarchyPath,
  dashboardPinId,
  name,
  dashboardMetaId,
  description,
  category,
  summary,
  favorite = false,
  homePage = false,
  filterJson = null,
  thumbnail = null,
  deviceId = null,
  userEmailId
) {
  const url = `${baseURL}${hierarchyPath}/dashboards/pins/${dashboardPinId}`;

  const pinConfig = {
    name,
    dashboardMetaId,
    description,
    category,
    summary,
    favorite,
    homePage,
    thumbnail,
    filterJson,
    deviceId,
  };
  return fetchPut(url, pinConfig, { userEmailId }).catch(error => {
    throw new I18NError(messages.errorUpdatingDashboardPin, {
      message: formatErrorResponse(error),
    });
  });
}

export function deleteDashboardPin(hierarchyPath, dashboardPinId, userEmailId) {
  // TODO we won't need this once we migrate Dashboard tab to v2 (we need to be able to construct hierarchyPath based on the GET response)
  const url = `${baseURL}${hierarchyPath}/dashboards/pins/${dashboardPinId}`;

  return fetchDelete(url, null, { userEmailId }).catch(error => {
    throw new I18NError(messages.errorRemovingDashboardPin, {
      message: formatErrorResponse(error),
    });
  });
}
